import axios from "axios";
import ENV from "../config/env";
import encryptedStorage from "./encryptedStorage";

const axiosInstance = axios.create({
  baseURL: ENV.API_HOST,
});

const SCHEME_KEY = "zenithScheme";

const readStoredScheme = () => {
  try {
    return window.localStorage.getItem(SCHEME_KEY);
  } catch {
    return null;
  }
};

// The scheme Zenith requests are scoped to, sent as x-level1-resource; null
// means the customer's home scheme (the API's default). Held per tab so another
// tab's switch can't rescope this one's requests; stored so a reload keeps it.
let scheme = readStoredScheme();
const schemeListeners = new Set<() => void>();

export const getScheme = () => scheme;

// For useSyncExternalStore: re-renders whatever reads the scheme on a switch.
export const subscribeScheme = (listener: () => void) => {
  schemeListeners.add(listener);
  return () => {
    schemeListeners.delete(listener);
  };
};

export const setScheme = (id: string | null) => {
  scheme = id;
  try {
    if (id) window.localStorage.setItem(SCHEME_KEY, id);
    else window.localStorage.removeItem(SCHEME_KEY);
  } catch {
    // The selection then lasts until reload.
  }
  schemeListeners.forEach(listener => listener());
};

axiosInstance.interceptors.request.use((requestConfig) => {
  const newConfig = { ...requestConfig };
  const token = encryptedStorage.getItem("token");

  if (token && newConfig.headers)
    newConfig.headers.Authorization = `Bearer ${token}`;

  if (scheme && newConfig.headers)
    newConfig.headers["x-level1-resource"] = scheme;

  return newConfig;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const { pathname: url } = window.location;
    const { status, config, data } = error.response;

    // A selected scheme the customer no longer holds water in (its last right
    // transferred away): start over in the home scheme. The API's Level1Resource
    // middleware sends this code.
    if (status === 403 && scheme && data?.code === "E_UNHELD_SCHEME") {
      setScheme(null);
      window.location.reload();
    }

    if (status === 401) {
      if (url.startsWith("/zenith")) {
        window.location.href = config.headers.Authorization
          ? "/zenith/logout"
          : "/zenith/login";
      } else {
        window.location.href = config.headers.Authorization
          ? "/polestar/logout"
          : "/polestar/login";
      }
    }
    throw error;
  }
);

export default axiosInstance;
